// Card32Result.js
import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { card32result } from '../../../store/slices/casinoResultsSlice';
import { useDispatch, useSelector } from 'react-redux';


// ─── helpers ────────────────────────────────────────────────────────────────

// FIX 1: parseHands — comma-separated multiple cards support
const parseHands = (hand = '') => {
  if (!hand) return [];
  return hand.split(',').map(h => {
    h = h.trim();
    if (h.length < 3) return { value: '?', suit: '?', color: 'black' };

    const suitCode = h.slice(-1);
    const value    = h.slice(0, -2);

    const suitMap = {
      C: { suit: '♣', color: 'black' },
      D: { suit: '♦', color: 'red'   },
      H: { suit: '♥', color: 'red'   },
      S: { suit: '♠', color: 'black' },
    };

    const { suit, color } = suitMap[suitCode] || { suit: '?', color: 'black' };
    return { value, suit, color };
  });
};

const formatDateTime = (iso = '') => {
  if (!iso) return '';
  const [datePart, timePart] = iso.split('T');
  if (!datePart) return iso;
  const [y, m, d] = datePart.split('-');
  const time = (timePart || '').split('.')[0];
  return `${d}/${m}/${y} ${time}`;
};

const formatWinnerLabel = (winnerType = '') =>
  winnerType.replace(/^PLAYER(\d+)$/, 'Player $1');

// FIX 2: buildPlayers — winnerType ?? winner fallback + multi-card support
const buildPlayers = (data = {}) => {
  const winnerType = data.winnerType ?? data.winner ?? '';

  const slots = [
    { label: 'Player 8',  hand: data.player1Hand, total: data.player1Total, key: 'PLAYER8'  },
    { label: 'Player 9',  hand: data.player2Hand, total: data.player2Total, key: 'PLAYER9'  },
    { label: 'Player 10', hand: data.player3Hand, total: data.player3Total, key: 'PLAYER10' },
    { label: 'Player 11', hand: data.player4Hand, total: data.player4Total, key: 'PLAYER11' },
  ];

  return slots.map(({ label, hand, total, key }) => {
    const cards = parseHands(hand);
    return {
      label,
      score: total,
      cards,
      card: cards[0] || { value: '?', suit: '?', color: 'black' }, // backward compat
      isWinner: winnerType === key,
    };
  });
};

// FIX 3: buildOddEvenFromParity — player8Parity etc. se oddEven build karo
const buildOddEvenFromParity = (data = {}) => {
  const fmt = (num, parity = '') =>
    `${num} : ${parity.charAt(0).toUpperCase() + parity.slice(1).toLowerCase()}`;

  const oddEven  = `${fmt(8, data.player8Parity)} | ${fmt(9, data.player9Parity)}`;
  const oddEven2 = `${fmt(10, data.player10Parity)} | ${fmt(11, data.player11Parity)}`;
  return { oddEven, oddEven2 };
};

// Fallback: purana outcome string parser (agar parity fields na ho)
const parseOutcome = (outcome = '') => {
  const parts = outcome.split(',');

  const parityParts = parts.slice(1, 5).map(p => {
    const [playerRaw, parity = ''] = p.split('_');
    const num = playerRaw.replace('PLAYER', '');
    return `${num} : ${parity.charAt(0).toUpperCase() + parity.slice(1).toLowerCase()}`;
  });

  const oddEven  = parityParts.slice(0, 2).join(' | ');
  const oddEven2 = parityParts.slice(2, 4).join(' | ');

  return { oddEven, oddEven2 };
};

const transformApiData = (apiData) => {
  const data = apiData?.data ?? apiData;
  if (!data?.id && !data?.roundId) return null;

  const players = buildPlayers(data);

  // FIX 3: player8Parity available ho toh use karo, warna outcome string fallback
  const { oddEven, oddEven2 } = data.player8Parity
    ? buildOddEvenFromParity(data)
    : parseOutcome(data.outcome || '');

  const blackCount = players.filter(p => p.card.color === 'black').length;
  const redCount   = players.length - blackCount;
  const blackRedLabel =
    blackCount === 4 ? 'All Black' :
    redCount   === 4 ? 'All Red'   :
    blackCount === 3 ? 'Three Black One Red' :
    redCount   === 3 ? 'Three Red One Black' :
    'Two Black Two Red';

  // const totalLabel  = `${players[2]?.label} & ${players[3]?.label} Total`;
  // const minScore    = Math.min(...players.map(p => p.score ?? Infinity));
  // const singleLabel = (players.find(p => p.score === minScore)?.label || '').replace('Player ', '');

  // FIX 2: winner field use karo formatWinnerLabel mein
  const winnerRaw = data.winnerType ?? data.winner ?? '';

  return {
    roundId:   String(data.id ?? data.roundId ?? ''),
    matchTime: formatDateTime(data.settledAt),
    winner:    formatWinnerLabel(winnerRaw),
    oddEven,
    oddEven2,
    blackRed:  blackRedLabel,
    // total:     totalLabel,
    // single:    singleLabel,
    players,
  };
};

// ─── CardBox ─────────────────────────────────────────────────────────────────

const CardBox = ({ value, suit, color = 'red' }) => (
  <Box sx={{
    width: '40px',
    height: '52px',
    border: '2px solid #c8a951',
    borderRadius: '5px',
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    fontWeight: 700,
    color: color === 'red' ? '#cc0000' : '#111111',
    lineHeight: 1.1,
    flexShrink: 0,
    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
  }}>
    <span style={{ fontSize: '15px', fontWeight: 800 }}>{value}</span>
    <span style={{ fontSize: '13px', marginTop: '1px' }}>{suit}</span>
  </Box>
);

// ─── ResultModal ─────────────────────────────────────────────────────────────

const ResultModal = ({ open, onClose, resultData }) => {
  if (!open) return null;
  if (!resultData) return null;

  const data = resultData;

  return (
    <Box
      onClick={onClose}
      sx={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.65)',
        zIndex: 1400,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '40px',
      }}
    >
      <Box
        onClick={e => e.stopPropagation()}
        sx={{
          width: '100%',
          maxWidth: '440px',
          background: '#ffffff',
          borderRadius: '2px',
          overflow: 'hidden',
          boxShadow: '0 6px 32px rgba(0,0,0,0.4)',
        }}
      >
        {/* ── Header ── */}
        <Box sx={{
          background: '#2c3e50',
          padding: '10px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: '14px', letterSpacing: '0.3px' }}>
            32 Cards Result
          </Typography>
          <Box
            onClick={onClose}
            sx={{
              width: '22px', height: '22px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              '&:hover': { background: 'rgba(255,255,255,0.25)' },
            }}
          >
            <Typography sx={{ color: '#ffffff', fontSize: '14px', lineHeight: 1 }}>✕</Typography>
          </Box>
        </Box>

        {/* ── Round ID & Match Time ── */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '6px 14px',
          borderBottom: '1px solid #e8e8e8',
          background: '#fafafa',
        }}>
          <Typography sx={{ fontSize: '11px', color: '#555', fontWeight: 600 }}>
            Round Id: {data.roundId}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: '#555', fontWeight: 600 }}>
            Match Time: {data.matchTime}
          </Typography>
        </Box>

        {/* ── Players ── */}
        <Box sx={{ padding: '14px 16px 8px' }}>
          {(data.players || []).map((player, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginBottom: i < (data.players.length - 1) ? '12px' : '4px',
              }}
            >
              {/* Player label + score */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px' }}>
                <Typography sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: player.isWinner ? '#e65100' : '#333333',
                }}>
                  {player.label}
                </Typography>
                <Typography sx={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: player.isWinner ? '#e65100' : '#333333',
                }}>
                  - {player.score}
                </Typography>
              </Box>

              {/* FIX 1: Multiple cards render */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {(player.cards && player.cards.length > 0 ? player.cards : [player.card]).map((c, ci) => (
                  <CardBox
                    key={ci}
                    value={c.value}
                    suit={c.suit}
                    color={c.color}
                  />
                ))}
                {player.isWinner && (
                  <Box sx={{ fontSize: '24px', lineHeight: 1 }}>🏆</Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        {/* ── Divider ── */}
        <Box sx={{ height: '1px', background: '#e0e0e0', margin: '0 16px' }} />

        {/* ── Result Summary ── */}
        <Box sx={{ padding: '10px 16px 14px', textAlign: 'center' }}>
          {[
            { label: 'Winner',    value: data.winner   },
            { label: 'Odd/Even',  value: data.oddEven  },
            { label: '',          value: data.oddEven2 },
            // { label: 'Black/Red', value: data.blackRed },
            // { label: 'Total',     value: data.total    },
            // { label: 'Single',    value: data.single   },
          ].map((row, i) => (
            <Typography
              key={i}
              sx={{ fontSize: '13px', color: '#333333', lineHeight: 1.9 }}
            >
              {row.label ? `${row.label} ${row.value}` : row.value}
            </Typography>
          ))}
        </Box>

        {/* ── Footer ── */}
        <Box sx={{
          borderTop: '1px solid #eeeeee',
          padding: '7px 14px',
          display: 'flex',
          justifyContent: 'space-between',
          background: '#fafafa',
        }}>
          <Typography sx={{ fontSize: '11px', color: '#999999' }}>
            {data.roundId}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: '#999999' }}>
            {data.winner}
          </Typography>
        </Box>

      </Box>
    </Box>
  );
};

// ─── Card32Result ─────────────────────────────────────────────────────────────

const Card32Result = ({ open, onClose, resultData }) => {
  const dispatch = useDispatch();
  const apiData  = useSelector((state) => state.casinoResults.card32);

  const roundId = resultData?._raw?.id || resultData?.roundId || resultData?.id;

  useEffect(() => {
    if (open) {
      dispatch(card32result());
    }
  }, [open, dispatch]);

const modalResult = React.useMemo(() => {
  // Case 1: resultData already has _raw (socket/live shape)
  if (resultData?._raw) {
    const transformed = transformApiData(resultData._raw);
    if (transformed) return transformed;
  }

  // Case 2: resultData itself is the raw round object (View All history row)
  if (resultData && !resultData.players) {
    const transformed = transformApiData(resultData);
    if (transformed) return transformed;
  }

  // Case 3: resultData is already fully transformed (has players array)
  if (resultData?.players) {
    return resultData;
  }

  // Case 4: fallback to redux apiData — but ONLY if it matches the requested round
  if (apiData) {
    const transformed = transformApiData(apiData);
    if (transformed && (!roundId || transformed.roundId === String(roundId))) {
      return transformed;
    }
  }

  return null;
}, [resultData, apiData, roundId]);

  return (
    <ResultModal
      open={open}
      onClose={onClose}
      resultData={modalResult}
    />
  );
};

export default Card32Result;